/**
 * Request cache utility to prevent duplicate API calls
 * If the same request is made while one is in-flight, it reuses the same promise
 */

const requestCache = new Map();

/**
 * Generate a cache key from request parameters
 */
const getCacheKey = (method, url, params = {}, data = {}) => {
  const paramsStr = JSON.stringify(params);
  const dataStr = JSON.stringify(data);
  return `${method}:${url}:${paramsStr}:${dataStr}`;
};

/**
 * Clear cache entry for a specific request
 */
export const clearCacheEntry = (method, url, params = {}, data = {}) => {
  const key = getCacheKey(method, url, params, data);
  requestCache.delete(key);
};

/**
 * Clear all cached requests
 */
export const clearAllCache = () => {
  requestCache.clear();
};

/**
 * Wrapper for axios requests that caches in-flight requests
 * @param {Function} axiosCall - The axios call function (e.g., axios.get, axios.post)
 * @param {string} url - The request URL
 * @param {Object} config - Axios config object
 * @param {number} cacheTimeout - How long to cache the result in ms (default: 0 = only cache in-flight)
 * @returns {Promise} - The axios promise
 */
export const cachedRequest = async (axiosCall, url, config = {}, cacheTimeout = 0) => {
  const method = config.method || 'GET';
  const params = config.params || {};
  const data = config.data || {};
  const key = getCacheKey(method, url, params, data);

  // Check if there's an in-flight request
  if (requestCache.has(key)) {
    const cached = requestCache.get(key);
    
    // If it's an in-flight promise, return it
    if (cached.promise) {
      console.log(`🔄 Reusing in-flight request: ${method} ${url}`);
      return cached.promise;
    }
    
    // If it's a cached result and still valid, return it
    if (cacheTimeout > 0 && cached.result && cached.timestamp) {
      const age = Date.now() - cached.timestamp;
      if (age < cacheTimeout) {
        console.log(`💾 Returning cached result: ${method} ${url}`);
        return Promise.resolve(cached.result);
      }
    }
  }

  // Create new request
  console.log(`📤 Making new request: ${method} ${url}`);
  const promise = axiosCall(url, config)
    .then(response => {
      const cached = requestCache.get(key);
      if (cached) {
        // Store result if caching is enabled
        if (cacheTimeout > 0) {
          cached.result = response;
          cached.timestamp = Date.now();
        }
        // Clear the promise since request is complete
        cached.promise = null;
      }
      return response;
    })
    .catch(error => {
      // Remove from cache on error
      requestCache.delete(key);
      throw error;
    });

  // Store the in-flight promise
  requestCache.set(key, { promise, result: null, timestamp: null });

  return promise;
};

/**
 * Helper function to create cached axios methods
 */
export const createCachedAxios = (axiosInstance) => {
  return {
    get: (url, config = {}) => cachedRequest(axiosInstance.get.bind(axiosInstance), url, { ...config, method: 'GET' }),
    post: (url, data, config = {}) => cachedRequest(axiosInstance.post.bind(axiosInstance), url, { ...config, method: 'POST', data }),
    put: (url, data, config = {}) => cachedRequest(axiosInstance.put.bind(axiosInstance), url, { ...config, method: 'PUT', data }),
    patch: (url, data, config = {}) => cachedRequest(axiosInstance.patch.bind(axiosInstance), url, { ...config, method: 'PATCH', data }),
    delete: (url, config = {}) => cachedRequest(axiosInstance.delete.bind(axiosInstance), url, { ...config, method: 'DELETE' }),
  };
};

